import React, { useEffect } from 'react';
import { styled as materialStyled } from '@mui/material/styles';
import { Box, Button, Dialog, DialogContent, DialogTitle, Divider, Grid, Skeleton, Typography } from '@mui/material';
import CloseIcon from '../../../assets/img/close-icon-black.svg';
import { CompanyDetails, Subordinate, subordinateActions } from '../../../redux/subordinate/subordinateSlice';
import CloseIconCross from '../../../assets/img/cancel-circle.svg';
import ArchievedSvg from '../../../assets/img/delivery-box-02.svg';
import TickSvg from '../../../assets/img/check-tick-02.svg';
import { theme } from '../../../theme/theme';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import EditSubordinatePermissionsModel from '../EditSubordinatePermissionsModel/EditSubordinatePermissionsModel';
// eslint-disable-next-line max-len
import ViewSubordinatePermissionModel from '../EditSubordinatePermissionsModel/ViewSubordinatePermission/ViewSubordinatePermissionModel';
import { UserStatus } from '../../../utils/enums/user-status.enum';

interface titleKeys {
  systemId: string;
  firstName: string;
  familyName: string;
  email: string;
  status: string;
  nationality: string;
  mobile: string;
  lastLogin: string;
  userSince: string;
  companyName: string;
  country: string;
  role: string;
  website: string;
}
export interface TitleProps {
  key: keyof titleKeys;
  name: string;
  textTransform?: 'capitalize' | 'none' | 'uppercase' | 'lowercase';
}

const titles: TitleProps[] = [
  { key: 'systemId', name: 'UserID', textTransform: 'uppercase' },
  { key: 'firstName', name: 'First Name', textTransform: 'capitalize' },
  { key: 'familyName', name: 'Last Name', textTransform: 'capitalize' },
  { key: 'email', name: 'Email Address', textTransform: 'none' },
  { key: 'status', name: 'Status', textTransform: 'capitalize' },
  { key: 'nationality', name: 'Nationality', textTransform: 'capitalize' },
  { key: 'mobile', name: 'Mobile', textTransform: 'none' },
  { key: 'lastLogin', name: 'Last Log-In', textTransform: 'none' },
  { key: 'userSince', name: 'User Since', textTransform: 'none' },
];
const nccUsertitles: TitleProps[] = [
  { key: 'companyName', name: 'Company Name', textTransform: 'none' },
  { key: 'country', name: 'Country', textTransform: 'none' },
  { key: 'role', name: 'Your Role in the Company', textTransform: 'none' },
  { key: 'website', name: 'Company Website', textTransform: 'none' },
];
const CustomDialogTitle = materialStyled(DialogTitle)(() => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '12px 12px 12px 24px',
}));
const CustomDialogContent = materialStyled(DialogContent)(() => ({
  padding: '0px',
}));
const CustomDialog = materialStyled(Dialog)(() => ({
  backgroundColor: 'rgba(0, 0, 0, 0.50)',
  boxShadow: '0px 0px 24px 0px rgba(0, 0, 0, 0.10)',
  '& .MuiDialog-paper': {
    borderRadius: '12px',
    margin: '0px',
    maxWidth: '780px',
    width: '70%',
  },
}));
const CustomButton = materialStyled(Button)(() => ({
  borderRadius: '8px',
  padding: '8px 12px',
  boxShadow: '0px 12px 12px 0px rgba(255, 255, 255, 0.12) inset, 0px -2px 2px 0px rgba(48, 48, 48, 0.10) inset',
}));
const CustomGridItem = materialStyled(Grid)(() => ({
  paddingBottom: '12px',
  overflowWrap: 'break-word',
}));
const CustomGridContainer = materialStyled(Grid)(() => ({
  padding: '24px',
  display: 'flex',
  justifyContent: 'flex-end',
}));

const getStatusIcon = (status: string): string | undefined => {
  switch (status.toLowerCase()) {
    case 'active':
      return TickSvg;
    case 'inactive':
      return CloseIconCross;
    case 'archived':
      return ArchievedSvg;
    default:
      return undefined;
  }
};

const getStatusStyle = (status: string) => {
  const baseStyles = {
    maxHeight: '24px',
    padding: '4px 10px 4px 6px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: '29px',
    gap: '4px',
  };

  switch (status.toLowerCase()) {
    case 'active':
      return {
        ...baseStyles,
        background: theme.palette.shades.successBrightLight,
      };
    case 'inactive':
      return {
        ...baseStyles,
        background: theme.palette.shades.errorBrightLight,
      };
    case 'archived':
      return {
        ...baseStyles,
        background: theme.palette.shades.warningBrightLight,
      };
    default:
      return baseStyles;
  }
};

const getStatusTextColor = (status: string): string => {
  switch (status.toLowerCase()) {
    case 'active':
      return theme.palette.shades.successBrightMain;
    case 'inactive':
      return theme.palette.shades.errorBrightMain;
    case 'archived':
      return theme.palette.shades.warningBrightMain;
    default:
      return 'inherit';
  }
};

interface SubordinatesDetailsProps {
  open: boolean;
  handleClose: () => void;
  isLoading: boolean;
  subordinate: Subordinate | null;
}

const SubordinatesDetails = ({ open, handleClose, isLoading, subordinate }: SubordinatesDetailsProps) => {
  const dispatch = useAppDispatch();
  const { componentStatus } = useAppSelector((state) => state.subordinate);
  const [editPemission, setEditPermission] = React.useState<boolean>(false);
  const [data, setData] = React.useState<Subordinate | null>(null);

  const fullName = `${subordinate?.firstName} ${subordinate?.familyName}`;
  const isArchived = subordinate?.status === UserStatus.ARCHIVED;

  const formatData = (subordinateData: Subordinate | null): Subordinate | null => {
    if (subordinateData) {
      if (isArchived) {
        return {
          ...subordinateData,
          userSince: new Date(subordinateData?.email.split('_')[1]).toLocaleDateString('en-GB'),
          email: subordinateData?.email.split('_')[0],
        };
      }
      return subordinateData;
    }
    return null;
  };

  const openEditPermission = () => {
    setEditPermission(true);
  };

  const handleEditPermission = () => {
    handleClose();
    dispatch(subordinateActions.openEditSubordinatePermissionsModal());
  };
  const handleDeleteSubordinate = () => {
    if (data) {
      handleClose();
      dispatch(subordinateActions.setSubordinateToDelete(data));
      dispatch(subordinateActions.openDeleteSubordinateModal());
    }
  };

  useEffect(() => {
    setData(formatData(subordinate));
  }, [subordinate]);
  return (
    <CustomDialog open={open} aria-labelledby="alert-dialog-title" aria-describedby="alert-dialog-description">
      {isLoading ? (
        <>
          {' '}
          <CustomDialogTitle id="alert-dialog-title">
            <Typography variant="title2Bold">
              <Skeleton width={120} height={30} />
            </Typography>
            <Box component="span" display="inline-block" ml="auto">
              <Button
                startIcon={<Box component="img" src={CloseIcon} alt="close" aria-label="closeIcon" />}
                onClick={handleClose}
              />
            </Box>
          </CustomDialogTitle>
          <Divider />
          <CustomDialogContent>
            <Skeleton variant="rectangular" height={80} />
          </CustomDialogContent>
          <Divider />
          <CustomGridContainer container spacing={2} justifyContent="center" sx={{ p: 2 }}>
            <Grid item>
              <Skeleton variant="rectangular" width={120} height={40} />
            </Grid>
            <Grid item>
              <Skeleton variant="rectangular" width={120} height={40} />
            </Grid>
          </CustomGridContainer>
        </>
      ) : (
        <>
          {' '}
          <CustomDialogTitle id="alert-dialog-title">
            <Typography variant="title2Bold" textTransform="capitalize">
              {fullName}
            </Typography>
            <Button
              startIcon={<Box component="img" src={CloseIcon} alt="close" aria-label="closeIcon" />}
              onClick={handleClose}
            />
          </CustomDialogTitle>
          <Divider />
          <CustomDialogContent>
            <CustomGridContainer container>
              {titles.map(({ key, name, textTransform }) => (
                <CustomGridItem item xs={key === 'systemId' ? 12 : 6} key={key}>
                  <Typography variant="textXS" color="#6F7C8E">
                    {(() => {
                      if (key === 'userSince') {
                        return isArchived ? 'Archived Date' : name;
                      }
                      return name;
                    })()}
                  </Typography>
                  <br />
                  {key === 'status' ? (
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box
                        sx={{
                          ...getStatusStyle(data?.status ?? ''),
                        }}
                      >
                        <Box
                          component="img"
                          src={getStatusIcon(data?.status ?? '')}
                          alt={data?.status ?? ''}
                          sx={{ width: 12, height: 12 }}
                        />
                        <Typography
                          variant="textSM"
                          textTransform={textTransform}
                          sx={{ color: getStatusTextColor(data?.status ?? '') }}
                        >
                          {String(data?.[key as keyof Subordinate] ?? '').toLowerCase()}
                        </Typography>
                      </Box>
                    </Box>
                  ) : (
                    <Typography variant="textBase" textTransform={textTransform}>
                      {String(data?.[key as keyof Subordinate] ?? '').toLowerCase()}
                    </Typography>
                  )}
                </CustomGridItem>
              ))}
              {!subordinate?.isNccUser &&
                (editPemission ? (
                  <EditSubordinatePermissionsModel permissions={data?.permission} />
                ) : (
                  <ViewSubordinatePermissionModel permissions={data?.permission} />
                ))}
            </CustomGridContainer>
            <Divider sx={{ mx: 3 }} />
            {subordinate?.isNccUser && (
              <CustomGridContainer container>
                {nccUsertitles.map(({ key, name, textTransform }) => (
                  <CustomGridItem item xs={key === 'systemId' ? 12 : 6} key={key}>
                    <Typography variant="textXS" color="#6F7C8E">
                      {name}
                    </Typography>
                    <br />
                    <Typography variant="textBase" textTransform={textTransform}>
                      {String(subordinate.companyDetails?.[key as keyof CompanyDetails] ?? '')}
                    </Typography>
                  </CustomGridItem>
                ))}
              </CustomGridContainer>
            )}
          </CustomDialogContent>{' '}
          <Divider />
          {!isArchived && (
            <Grid
              container
              sx={{ padding: '16px 12px', display: 'flex', justifyContent: 'flex-end', background: '#F6F7F9' }}
              gap={1}
            >
              {editPemission ? (
                <Grid item>
                  <CustomButton
                    variant="contained"
                    disabled={componentStatus.newSubordinatePermissions?.length === 0}
                    onClick={handleEditPermission}
                    aria-label="editSubordinate"
                    sx={{
                      backgroundColor: '#3348FF',
                      '&:hover': {
                        backgroundColor: '#007AFF',
                      },
                    }}
                  >
                    <Typography variant="bodyRegular">Update Permissions</Typography>
                  </CustomButton>
                </Grid>
              ) : (
                <>
                  <Grid item>
                    <CustomButton onClick={handleDeleteSubordinate} variant="contained" color="white">
                      <Typography variant="calloutBold" color="#DF0C3D">
                        Delete Subordinate
                      </Typography>
                    </CustomButton>
                  </Grid>
                  {!subordinate?.isNccUser && (
                    <Grid item>
                      <CustomButton
                        variant="contained"
                        onClick={openEditPermission}
                        aria-label="editSubordinate"
                        sx={{
                          backgroundColor: '#3348FF',
                          '&:hover': {
                            backgroundColor: '#007AFF',
                          },
                        }}
                      >
                        <Typography variant="bodyRegular">Edit Permissions</Typography>
                      </CustomButton>
                    </Grid>
                  )}
                </>
              )}
            </Grid>
          )}
        </>
      )}
    </CustomDialog>
  );
};

export default SubordinatesDetails;
